// Detalhe de um lançamento na Rampa: cabeçalho (foto, código, gramatura, início
// de vendas), 6 indicadores com meta (ROB, Volume, PCA, Margem de contribuição,
// Devolução, Preço médio), lançamento mensal (12 meses) e relatório PT/EN.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { canFillRamps, isAdmin } from '../lib/rbac';
import { mesLiberado, textoLiberacao } from '../shared/fechamento';
import { calcularAncora, gramaturasDiferem, ROTULO_ANCORA_PCT, ROTULO_ANCORA_PCT_EN, legendaPco, type Ancora } from '../shared/ancoragem';
import { vendaUnLojaSemana, ROTULO_UN_LOJA_SEMANA, ROTULO_UN_LOJA_SEMANA_EN } from '../shared/rampaIndicadores';
import { FloatingBack } from '../components/FloatingBack';
import { uploadRampImage } from '../lib/photos';
import { syncProductPhoto } from '../lib/productLink';
import { buildReportHtml, printReport, prepararFoto, EN_LABELS, type ReportIndicator } from '../lib/report';
import {
  loadRamp,
  loadRampEntries,
  upsertRampEntry,
  updateRamp,
  deleteRamp,
  rampMonthsList,
  monthLabel,
  monthKey,
  type LaunchRamp,
  type RampEntry,
} from '../lib/launchRamps';

const brl = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const brl2 = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numf = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dec1 = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

const INDICADORES = [
  { key: 'rob', label: 'ROB', fmt: brl, lower: false },
  { key: 'volume', label: 'Volume', fmt: numf, lower: false },
  { key: 'pca', label: 'PCA', fmt: numf, lower: false },
  { key: 'mc', label: 'Margem de contribuição (%)', fmt: pct, lower: false },
  { key: 'devolucao', label: '% Devolução', fmt: pct, lower: true },
  { key: 'preco_medio', label: 'Preço médio', fmt: brl2, lower: false },
  // Calculado (volume ÷ PCA ÷ gramatura ÷ 4 semanas); não é digitado e ainda não tem meta.
  { key: 'un_loja_semana', label: ROTULO_UN_LOJA_SEMANA, fmt: dec1, lower: false },
] as const;

interface IndDef { key: string; label: string; fmt: (n: number | null | undefined) => string; lower: boolean }

export function RampaProduto() {
  const { id } = useParams();
  const { grants, userId, fullName } = useAuth();
  const canFill = grants ? canFillRamps(grants) : false;
  // Só admin pode preencher ANTES do fechamento do mês (ex.: se o fechamento sair mais cedo).
  const podeAntecipar = grants ? isAdmin(grants) : false;
  const [ramp, setRamp] = useState<LaunchRamp | null>(null);
  const [entries, setEntries] = useState<RampEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editMeta, setEditMeta] = useState(false);
  // Ancoragem de preço no relatório: desligada por padrão (os preços ainda são de teste).
  const [incluirAncora, setIncluirAncora] = useState(false);

  async function reload() {
    if (!id) return;
    const [r, e] = await Promise.all([loadRamp(id), loadRampEntries(id)]);
    setRamp(r);
    setEntries(e);
    setLoading(false);
  }
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const anchor = ramp ? ramp.salesStartDate ?? ramp.createdAt : null;
  const months = useMemo(
    () => (ramp && anchor ? rampMonthsList(anchor, ramp.planMonths) : []),
    [ramp, anchor],
  );
  const byMonth = useMemo(() => new Map(entries.map((e) => [e.refMonth, e])), [entries]);

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !ramp) return;
    setUploadingPhoto(true);
    const up = await uploadRampImage(ramp.orgId, ramp.id, file);
    if (up.error) { setUploadingPhoto(false); return alert(up.error); }
    const r = await updateRamp(ramp.id, { photo_url: up.url });
    // Mesmo produto (por código) na Saída de linha recebe a mesma foto.
    if (up.url) await syncProductPhoto(ramp.orgId, ramp.codigo, up.url);
    setUploadingPhoto(false);
    if (r.error) return alert(r.error);
    await reload();
  }

  if (loading) return <div className="center-note">Carregando…</div>;
  if (!ramp) return <div className="center-note">Lançamento não encontrado. <Link to="/rampa">Voltar</Link></div>;

  const inicioVendas = ramp.salesStartDate ? new Date(ramp.salesStartDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
  const thisMonth = monthKey(new Date());

  const ancora: Ancora = { nome: ramp.ancoraNome, gramatura: ramp.ancoraGramatura, elasticidade: ramp.ancoraElasticidade, base: ramp.ancoraBase };
  const temAncora = Boolean(ramp.ancoraNome) && ramp.ancoraElasticidade != null;
  const indicadores: IndDef[] = temAncora
    ? [...INDICADORES, { key: 'ancora_pct', label: ROTULO_ANCORA_PCT, fmt: pct, lower: false }]
    : [...INDICADORES];

  const metaFor = (k: string): number | null => {
    switch (k) {
      case 'rob': return ramp.metaRob;
      case 'volume': return ramp.metaVolume;
      case 'pca': return ramp.metaPca;
      case 'mc': return ramp.metaMc;
      case 'devolucao': return ramp.metaDevolucao;
      case 'preco_medio': return ramp.metaPrecoMedio;
      case 'ancora_pct': return ramp.ancoraElasticidade;
      default: return null;
    }
  };
  const valFor = (e: RampEntry | undefined, k: string): number | null => {
    if (!e) return null;
    switch (k) {
      case 'rob': return e.rob;
      case 'volume': return e.volume;
      case 'pca': return e.pca;
      case 'mc': return e.mc;
      case 'devolucao': return e.devolucao;
      case 'preco_medio': return e.precoMedio;
      case 'un_loja_semana': return vendaUnLojaSemana(e.volume, e.pca, ramp?.gramatura);
      case 'ancora_pct': return calcularAncora(ancora, ramp?.gramatura, e.precoMedio, e.ancoraPreco)?.pct ?? null;
      default: return null;
    }
  };

  async function baixarRelatorio(lang: 'pt' | 'en') {
    if (!ramp) return;
    const EN = lang === 'en';
    const indLabelsEn: Record<string, string> = {
      rob: 'Gross revenue (ROB)', volume: 'Volume', pca: 'PCA',
      mc: 'Contribution margin (%)', devolucao: 'Returns (%)', preco_medio: 'Average price',
      un_loja_semana: ROTULO_UN_LOJA_SEMANA_EN,
      ancora_pct: ROTULO_ANCORA_PCT_EN,
    };
    const comAncora = temAncora && incluirAncora;
    const indicators: ReportIndicator[] = indicadores.filter((i) => comAncora || i.key !== 'ancora_pct').map((ind) => ({
      title: EN ? indLabelsEn[ind.key] ?? ind.label : ind.label,
      meta: metaFor(ind.key),
      points: months.map((m) => ({ label: monthLabel(m), value: valFor(byMonth.get(m), ind.key) })),
      fmt: ind.fmt,
      lowerIsBetter: ind.lower,
    }));
    const header = EN
      ? [
          { label: 'Brand', value: ramp.brandName ?? '—' },
          { label: 'Code', value: ramp.codigo ?? '—' },
          { label: 'Size', value: ramp.gramatura ?? '—' },
          { label: 'Sales start', value: inicioVendas },
          { label: 'Tracking', value: `${ramp.planMonths} months` },
          ...(comAncora ? [{ label: 'Price anchor', value: `${ramp.ancoraNome} · ${pct(ramp.ancoraElasticidade)}` }] : []),
        ]
      : [
          { label: 'Marca', value: ramp.brandName ?? '—' },
          { label: 'Código', value: ramp.codigo ?? '—' },
          { label: 'Gramatura', value: ramp.gramatura ?? '—' },
          { label: 'Início das vendas', value: inicioVendas },
          { label: 'Acompanhamento', value: `${ramp.planMonths} meses` },
          ...(comAncora ? [{ label: 'Âncora de preço', value: `${ramp.ancoraNome} · ${pct(ramp.ancoraElasticidade)}` }] : []),
        ];
    const html = buildReportHtml({
      title: ramp.name,
      brand: ramp.brandName,
      subtitle: `${ramp.brandName ?? ''}${ramp.codigo ? ` · ${ramp.codigo}` : ''}`,
      tagline: EN ? 'Launch ramp — performance tracking' : 'Rampa de lançamento — acompanhamento',
      photoUrl: await prepararFoto(ramp.photoUrl),
      header,
      indicators,
      tableHead: [EN ? 'Month' : 'Mês', 'ROB', EN ? 'Volume' : 'Volume', 'PCA', EN ? 'Margin %' : 'Margem %', EN ? 'Returns %' : 'Devolução %', EN ? 'Avg price' : 'Preço médio', EN ? 'Units/store/wk' : 'Un./loja/sem.', ...(comAncora ? [EN ? 'Anchor price' : 'Preço âncora', EN ? 'PCO achieved' : 'PCO realizado', EN ? 'PCO suggested' : 'PCO sugerido', EN ? 'Gap vs suggestion' : 'Gap vs sugestão'] : [])],
      tableRows: months.map((m) => {
        const e = byMonth.get(m);
        return [
          monthLabel(m), brl(e?.rob ?? null), numf(e?.volume ?? null), numf(e?.pca ?? null),
          pct(e?.mc ?? null), pct(e?.devolucao ?? null), brl2(e?.precoMedio ?? null),
          dec1(vendaUnLojaSemana(e?.volume, e?.pca, ramp.gramatura)),
          ...(comAncora
            ? [
                brl2(e?.ancoraPreco ?? null),
                brl2(calcularAncora(ancora, ramp.gramatura, e?.precoMedio, e?.ancoraPreco)?.nossoUn ?? null),
                brl2(calcularAncora(ancora, ramp.gramatura, e?.precoMedio, e?.ancoraPreco)?.alvoUn ?? null),
                pct(calcularAncora(ancora, ramp.gramatura, e?.precoMedio, e?.ancoraPreco)?.gapPct ?? null),
              ]
            : []),
        ];
      }),
      sections: comAncora ? [{ title: EN ? 'Legend' : 'Legenda', items: [{ head: 'PCO', body: legendaPco(EN) }] }] : undefined,
      labels: EN ? EN_LABELS : undefined,
      footerNote: EN
        ? `Generated on ${new Date().toLocaleString('en-GB')} · New Product Workflow — Trevo Lácteos`
        : `Gerado em ${new Date().toLocaleString('pt-BR')} · Workflow de Novos Produtos — Trevo Lácteos`,
    }, true);
    printReport(html, `${EN ? 'launch-ramp' : 'rampa'}-${ramp.codigo ?? ramp.name}-${lang}`);
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="muted" style={{ fontSize: 12 }}>
            <Link to="/rampa">Rampa de lançamentos</Link> · {ramp.brandName ?? 'sem marca'}
          </div>
          <h1>{ramp.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {temAncora && (
            <label className="muted" style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }} title="Inclui o indicador de preço vs. âncora, as colunas de PCO e a legenda no relatório">
              <input type="checkbox" checked={incluirAncora} onChange={(e) => setIncluirAncora(e.target.checked)} style={{ minHeight: 0, width: 'auto' }} /> incluir ancoragem de preço
            </label>
          )}
          <button className="btn-ghost btn-sm" onClick={() => baixarRelatorio('pt')}>📄 Relatório PDF (PT)</button>
          <button className="btn-ghost btn-sm" onClick={() => baixarRelatorio('en')}>📄 Report PDF (EN)</button>
          <span className={`seal ${ramp.status === 'acompanhando' ? 'pend' : 'ok'}`}>
            {ramp.status === 'acompanhando' ? 'Acompanhando' : 'Encerrado'}
          </span>
        </div>
      </div>

      <div className="content">
        {/* Cabeçalho */}
        <div className="card card-pad disc-header">
          <div className="disc-header-imgwrap">
            <div className="disc-header-img">
              {ramp.photoUrl ? <img src={ramp.photoUrl} alt={ramp.name} /> : <span className="disc-noimg">sem foto</span>}
            </div>
            {canFill && (
              <label className="btn-ghost btn-sm" style={{ marginTop: 6, textAlign: 'center', cursor: 'pointer' }}>
                {uploadingPhoto ? 'Enviando…' : ramp.photoUrl ? '📷 Trocar foto' : '📷 Adicionar foto'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingPhoto} onChange={onPickPhoto} />
              </label>
            )}
          </div>
          <div className="disc-header-info">
            <div className="disc-kv"><span>Código</span><b>{ramp.codigo ?? '—'}</b></div>
            <div className="disc-kv"><span>Gramatura</span><b>{ramp.gramatura ?? '—'}</b></div>
            <div className="disc-kv"><span>Início das vendas</span><b>{inicioVendas}</b></div>
            <div className="disc-kv"><span>Acompanhamento</span><b>{ramp.planMonths} meses</b></div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="card card-pad">
          <div className="section-title"><h2>Indicadores vs. meta</h2></div>
          <div className="disc-charts">
            {indicadores.map((ind) => (
              <IndicatorChart
                key={ind.key}
                title={ind.label}
                fmt={ind.fmt}
                lowerIsBetter={ind.lower}
                meta={metaFor(ind.key)}
                months={months}
                data={months.map((m) => ({ m, v: valFor(byMonth.get(m), ind.key) }))}
              />
            ))}
          </div>
        </div>

        {/* Lançamentos mensais */}
        <div className="card card-pad">
          <div className="section-title">
            <h2>Lançamentos mensais</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              cada mês só é liberado <b>após o fechamento</b> (por volta do dia 10 do mês seguinte); meses passados podem ser preenchidos a qualquer momento
            </span>
          </div>
          <div className="table-wrap">
            <table className="disc-entries ramp-entries">
              <thead>
                <tr>
                  <th className="c-mes">Mês</th><th>ROB (R$)</th><th>Volume</th><th>PCA</th><th>MC (%)</th><th>% Devol.</th><th>Preço médio</th><th title="Volume ÷ PCA ÷ gramatura ÷ 4 semanas">Un./loja/sem.</th>{temAncora && <><th title="Preço ao consumidor do produto âncora no mês (R$/un.) — preencher todo mês">Âncora (R$/un.)</th><th title="Preço médio ÷ 0,72, convertido em unidade pela gramatura (o que o Comercial executou)">PCO realizado (R$/un.)</th><th title="Preço do âncora × elasticidade (o que deveríamos praticar)">PCO sugerido (R$/un.)</th><th title="PCO realizado ÷ PCO sugerido − 1. Vermelho = precificando abaixo; verde = acima">% Gap vs sugestão</th></>}<th className="c-save"></th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <RampRow
                    key={m}
                    month={m}
                    isThisMonth={m === thisMonth}
                    liberado={mesLiberado(m)}
                    podeAntecipar={podeAntecipar}
                    entry={byMonth.get(m) ?? null}
                    orgId={ramp.orgId}
                    rampId={ramp.id}
                    gramatura={ramp.gramatura}
                    ancora={temAncora ? ancora : null}
                    userId={userId}
                    fullName={fullName}
                    canFill={canFill}
                    onSaved={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {temAncora && <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}><b>Legenda:</b> {legendaPco()}</p>}
        </div>

        {/* Ancoragem de preço */}
        <AncoraCard ramp={ramp} canEdit={canFill} onSaved={reload} />

        {/* Metas + início de vendas */}
        {canFill && (
          editMeta ? (
            <MetasEditor ramp={ramp} onClose={() => setEditMeta(false)} onSaved={() => { setEditMeta(false); reload(); }} />
          ) : (
            <div className="card card-pad">
              <div className="section-title">
                <h2>Metas &amp; início das vendas</h2>
                <button className="btn-ghost btn-sm" onClick={() => setEditMeta(true)}>✏️ Editar</button>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                ROB {brl(ramp.metaRob)} · Volume {numf(ramp.metaVolume)} · PCA {numf(ramp.metaPca)} · MC {ramp.metaMc != null ? `${ramp.metaMc}%` : '—'} · Devol. {pct(ramp.metaDevolucao)} · Preço {brl2(ramp.metaPrecoMedio)}
              </div>
            </div>
          )
        )}

        {canFill && (
          <button
            className="linklike"
            style={{ color: 'var(--danger, #c0392b)' }}
            onClick={async () => {
              if (!confirm('Excluir este lançamento e todos os dados?')) return;
              const { error } = await deleteRamp(ramp.id);
              if (error) return alert(error);
              window.location.href = '/rampa';
            }}
          >
            Excluir lançamento
          </button>
        )}
      </div>

      <FloatingBack to="/rampa" label="Voltar à lista" />
    </>
  );
}

function RampRow({
  month, isThisMonth, liberado, podeAntecipar, entry, orgId, rampId, gramatura, ancora, userId, fullName, canFill: podePreencher, onSaved,
}: {
  month: string;
  isThisMonth: boolean;
  liberado: boolean; // o mês já fechou? (a partir do dia 10 do mês seguinte)
  podeAntecipar: boolean; // admin pode preencher antes do fechamento
  entry: RampEntry | null;
  orgId: string;
  rampId: string;
  gramatura: string | null;
  ancora: Ancora | null; // null = sem âncora configurada
  userId: string | null;
  fullName: string | null;
  canFill: boolean;
  onSaved: () => void;
}) {
  const [rob, setRob] = useState(entry?.rob?.toString() ?? '');
  const [vol, setVol] = useState(entry?.volume?.toString() ?? '');
  const [pca, setPca] = useState(entry?.pca?.toString() ?? '');
  const [mc, setMc] = useState(entry?.mc?.toString() ?? '');
  const [dev, setDev] = useState(entry?.devolucao?.toString() ?? '');
  const [preco, setPreco] = useState(entry?.precoMedio?.toString() ?? '');
  const [anc, setAnc] = useState(entry?.ancoraPreco?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const mark = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setDirty(true); };
  // Mês ainda em aberto (não fechou): bloqueado para todos, exceto admin.
  const travado = !liberado && !podeAntecipar;
  const canFill = podePreencher && !travado;
  const rotulo = liberado
    ? ''
    : travado
      ? ` 🔒 libera em ${textoLiberacao(month)}`
      : ` ⏳ em aberto (libera em ${textoLiberacao(month)}; admin pode antecipar)`;

  async function salvar() {
    setBusy(true);
    const { error } = await upsertRampEntry({
      orgId, rampId, refMonth: month,
      rob: numOrNull(rob), volume: numOrNull(vol), pca: numOrNull(pca),
      mc: numOrNull(mc), devolucao: numOrNull(dev), precoMedio: numOrNull(preco), ancoraPreco: numOrNull(anc),
      comentarios: entry?.comentarios ?? null, // a coluna de comentários saiu da tabela; preserva o que já existia
      byId: userId, byName: fullName,
    });
    setBusy(false);
    if (error) return alert(error);
    setDirty(false);
    onSaved();
  }

  return (
    <tr className={isThisMonth ? 'disc-row-now' : ''}>
      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {monthLabel(month)}
        {isThisMonth ? ' · atual' : ''}
        {rotulo && (
          <div className="muted" style={{ fontWeight: 400, fontSize: 10, lineHeight: 1.2 }} title={rotulo.trim()}>
            {travado ? '🔒' : '⏳'} libera {textoLiberacao(month)}
          </div>
        )}
      </td>
      <td><input type="number" value={rob} disabled={!canFill} onChange={(e) => mark(setRob)(e.target.value)} /></td>
      <td><input type="number" value={vol} disabled={!canFill} onChange={(e) => mark(setVol)(e.target.value)} /></td>
      <td><input type="number" value={pca} disabled={!canFill} onChange={(e) => mark(setPca)(e.target.value)} /></td>
      <td><input type="number" value={mc} disabled={!canFill} onChange={(e) => mark(setMc)(e.target.value)} /></td>
      <td><input type="number" value={dev} disabled={!canFill} onChange={(e) => mark(setDev)(e.target.value)} /></td>
      <td><input type="number" value={preco} disabled={!canFill} onChange={(e) => mark(setPreco)(e.target.value)} /></td>
      <td style={{ whiteSpace: 'nowrap' }} title="Volume ÷ PCA ÷ gramatura ÷ 4 semanas (calculado)">
        <b>{dec1(vendaUnLojaSemana(numOrNull(vol), numOrNull(pca), gramatura))}</b>
      </td>
      {ancora && (() => {
        const r = calcularAncora(ancora, gramatura, numOrNull(preco), numOrNull(anc));
        return (
          <>
            <td><input type="number" step="0.01" value={anc} disabled={!canFill} onChange={(e) => mark(setAnc)(e.target.value)} /></td>
            <td style={{ whiteSpace: 'nowrap' }}>{r ? brl2(r.nossoUn) : '—'}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{r?.alvoUn != null ? brl2(r.alvoUn) : '—'}</td>
            <td style={{ whiteSpace: 'nowrap', fontWeight: 700, color: r?.gapPct == null ? undefined : r.gapPct < 0 ? '#b91c1c' : '#15803d' }}
              title={r?.gapPct != null ? (r.gapPct < 0 ? 'Precificando ABAIXO da sugestão' : 'Precificando acima (ou na) sugestão') : undefined}>
              {r?.gapPct != null ? `${r.gapPct > 0 ? '+' : ''}${r.gapPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—'}
              {r?.gapPct == null ? '' : r.gapPct < 0 ? ' ✖' : ' ✔'}
            </td>
          </>
        );
      })()}
      <td>{canFill && <button className={dirty ? 'btn-primary btn-sm' : 'btn-ghost btn-sm'} disabled={busy} onClick={salvar}>{busy ? '…' : 'Salvar'}</button>}</td>
    </tr>
  );
}

/** Produto de ancoragem: o Marketing escolhe o item da concorrência e a elasticidade (meta) sobre ele. */
function AncoraCard({ ramp, canEdit, onSaved }: { ramp: LaunchRamp; canEdit: boolean; onSaved: () => void }) {
  const [edit, setEdit] = useState(false);
  const [nome, setNome] = useState(ramp.ancoraNome ?? '');
  const [elast, setElast] = useState(ramp.ancoraElasticidade?.toString() ?? '');
  const [gram, setGram] = useState(ramp.ancoraGramatura ?? '');
  const [base, setBase] = useState<'un' | 'kg'>(ramp.ancoraBase);
  const [busy, setBusy] = useState(false);
  const difere = gramaturasDiferem(ramp.gramatura, gram);

  async function salvar() {
    setBusy(true);
    const { error } = await updateRamp(ramp.id, {
      ancora_nome: nome.trim() || null,
      ancora_elasticidade: numOrNull(elast),
      ancora_gramatura: gram.trim() || null,
      ancora_base: difere ? base : 'un',
    });
    setBusy(false);
    if (error) return alert(error);
    setEdit(false);
    onSaved();
  }

  const el = ramp.ancoraElasticidade;
  const porBase = gramaturasDiferem(ramp.gramatura, ramp.ancoraGramatura)
    ? ` · elasticidade por ${ramp.ancoraBase === 'kg' ? 'kg' : 'unidade'}`
    : '';
  const resumo =
    ramp.ancoraNome && el != null
      ? `${ramp.ancoraNome} · meta: no mínimo ${pct(el)} do preço do âncora${ramp.ancoraGramatura ? ` · gramatura do âncora ${ramp.ancoraGramatura}` : ''}${porBase}`
      : 'Nenhum produto âncora definido.';

  if (!edit) {
    return (
      <div className="card card-pad">
        <div className="section-title">
          <h2>Produto de ancoragem (preço)</h2>
          {canEdit && <button className="btn-ghost btn-sm" onClick={() => setEdit(true)}>✏️ Editar</button>}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>{resumo}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Todo mês preencha o preço ao consumidor do âncora na tabela de lançamentos. {legendaPco()}
        </div>
      </div>
    );
  }
  return (
    <div className="card card-pad">
      <div className="section-title"><h2>Produto de ancoragem (preço)</h2></div>
      <div className="capa-grid">
        <label className="field"><span>Item da concorrência (âncora)</span><input value={nome} placeholder="ex.: Itambé Fit" onChange={(e) => setNome(e.target.value)} /></label>
        <label className="field"><span>Elasticidade — mínimo (% do preço do âncora)</span><input type="number" step="0.1" value={elast} placeholder="ex.: 90 ou 108 (mínimo desejado)" onChange={(e) => setElast(e.target.value)} /></label>
        <label className="field"><span>Gramatura do âncora (opcional)</span><input value={gram} placeholder={`vazio = mesma do nosso (${ramp.gramatura ?? '—'})`} onChange={(e) => setGram(e.target.value)} /></label>
      </div>
      {difere && (
        <div className="verdict bad" style={{ marginBottom: 10 }}>
          A gramatura do âncora ({gram}) é diferente da nossa ({ramp.gramatura}). A elasticidade deve valer por:
          <label style={{ marginLeft: 12 }}><input type="radio" checked={base === 'un'} onChange={() => setBase('un')} /> unidade (R$/un.)</label>
          <label style={{ marginLeft: 12 }}><input type="radio" checked={base === 'kg'} onChange={() => setBase('kg')} /> kg (R$/kg)</label>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn-ghost btn-sm" onClick={() => setEdit(false)}>Cancelar</button>
        <button className="btn-primary btn-sm" disabled={busy} onClick={salvar}>{busy ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </div>
  );
}

function MetasEditor({ ramp, onClose, onSaved }: { ramp: LaunchRamp; onClose: () => void; onSaved: () => void }) {
  const [sales, setSales] = useState(ramp.salesStartDate ?? '');
  const [rob, setRob] = useState(ramp.metaRob?.toString() ?? '');
  const [vol, setVol] = useState(ramp.metaVolume?.toString() ?? '');
  const [pca, setPca] = useState(ramp.metaPca?.toString() ?? '');
  const [mc, setMc] = useState(ramp.metaMc?.toString() ?? '');
  const [dev, setDev] = useState(ramp.metaDevolucao?.toString() ?? '');
  const [preco, setPreco] = useState(ramp.metaPrecoMedio?.toString() ?? '');
  const [busy, setBusy] = useState(false);

  async function salvar() {
    setBusy(true);
    const { error } = await updateRamp(ramp.id, {
      sales_start_date: sales || null,
      meta_rob: numOrNull(rob), meta_volume: numOrNull(vol), meta_pca: numOrNull(pca),
      meta_mc: numOrNull(mc), meta_devolucao: numOrNull(dev), meta_preco_medio: numOrNull(preco),
    });
    setBusy(false);
    if (error) return alert(error);
    onSaved();
  }

  return (
    <div className="card card-pad">
      <div className="section-title"><h2>Metas &amp; início das vendas</h2></div>
      <div className="capa-grid">
        <label className="field"><span>Início das vendas</span><input type="date" value={sales} onChange={(e) => setSales(e.target.value)} /></label>
        <label className="field"><span>Meta ROB (R$)</span><input type="number" value={rob} onChange={(e) => setRob(e.target.value)} /></label>
        <label className="field"><span>Meta Volume</span><input type="number" value={vol} onChange={(e) => setVol(e.target.value)} /></label>
        <label className="field"><span>Meta PCA</span><input type="number" value={pca} onChange={(e) => setPca(e.target.value)} /></label>
        <label className="field"><span>Meta Margem contrib. (%)</span><input type="number" value={mc} onChange={(e) => setMc(e.target.value)} /></label>
        <label className="field"><span>Meta % Devolução</span><input type="number" step="0.1" value={dev} onChange={(e) => setDev(e.target.value)} /></label>
        <label className="field"><span>Meta Preço médio (R$)</span><input type="number" step="0.01" value={preco} onChange={(e) => setPreco(e.target.value)} /></label>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
        <button className="btn-primary btn-sm" disabled={busy} onClick={salvar}>{busy ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </div>
  );
}

// Gráfico de indicador (linha + meta + linha do zero + rótulos).
function IndicatorChart({
  title, data, meta, months, fmt, lowerIsBetter = false,
}: {
  title: string;
  data: { m: string; v: number | null }[];
  meta: number | null;
  months: string[];
  fmt: (v: number | null) => string;
  lowerIsBetter?: boolean;
}) {
  const w = 340, h = 178, padL = 8, padR = 8, padT = 22, padB = 38;
  const vals = data.map((d) => d.v).filter((v): v is number => v != null);
  const all = meta != null ? [...vals, meta, 0] : [...vals, 0];
  const maxV = Math.max(1, ...all);
  const minV = Math.min(0, ...all);
  const span = maxV - minV || 1;
  const n = Math.max(1, months.length);
  const x = (i: number) => padL + (i * (w - padL - padR)) / Math.max(1, n - 1);
  const y = (v: number) => padT + (1 - (v - minV) / span) * (h - padT - padB);
  const pts = data.map((d, i) => (d.v == null ? null : `${x(i)},${y(d.v)}`)).filter((p): p is string => p != null).join(' ');
  const lastVal = [...data].reverse().find((d) => d.v != null)?.v ?? null;
  const hasNeg = minV < 0;

  return (
    <div className="disc-chart">
      <div className="disc-chart-title">{title}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="disc-chart-svg" role="img" aria-label={title}>
        {hasNeg && (
          <>
            <line x1={padL} x2={w - padR} y1={y(0)} y2={y(0)} stroke="var(--ink-faint, #9aa)" strokeWidth={1} />
            <text x={padL} y={y(0) - 2} fontSize="8" fill="var(--ink-faint, #9aa)">0</text>
          </>
        )}
        {meta != null && (
          <>
            <line x1={padL} x2={w - padR} y1={y(meta)} y2={y(meta)} stroke="var(--brand-green, #2e7d32)" strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={w - padR} y={y(meta) - 3} textAnchor="end" fontSize="9" fill="var(--brand-green, #2e7d32)">meta {fmt(meta)}</text>
          </>
        )}
        {pts && <polyline points={pts} fill="none" stroke="var(--accent, #2563eb)" strokeWidth={2} />}
        {data.map((d, i) => {
          if (d.v == null) return null;
          const neg = d.v < 0;
          const ly = neg ? y(d.v) + 11 : y(d.v) - 6;
          return (
            <g key={d.m}>
              <circle cx={x(i)} cy={y(d.v)} r={3} fill={neg ? 'var(--st-atrasada, #c0392b)' : 'var(--accent, #2563eb)'} />
              <text x={x(i)} y={ly} textAnchor="middle" fontSize="8.5" fontWeight="600" fill={neg ? 'var(--st-atrasada, #c0392b)' : 'var(--ink, #222)'}>{fmt(d.v)}</text>
            </g>
          );
        })}
        {months.map((m, i) => {
          const lx = x(i), ly = h - padB + 11;
          return (
            <text key={m} x={lx} y={ly} textAnchor="end" fontSize="7.5" fill="var(--ink-faint, #888)" transform={`rotate(-40 ${lx} ${ly})`}>{monthLabel(m)}</text>
          );
        })}
      </svg>
      <div className="disc-chart-foot">
        Último: <b>{fmt(lastVal)}</b>{meta != null ? <> · meta <b>{fmt(meta)}</b>{lowerIsBetter ? ' (menor é melhor)' : ''}</> : null}
      </div>
    </div>
  );
}
